import React, { useState } from 'react';
import { 
  Upload,
  Download,
  FileText,
  CheckCircle,
  AlertCircle,
  X,
  Trash2,
  Edit,
  Search,
  RefreshCw,
  User,
  Eye,
  Users
} from 'lucide-react';
import { contractService } from '../../services/contractService';
import { Contract } from '../../types/contract';
import { formatDate, toISOString, fromISOString } from '../../utils/dateUtils';

interface ImportResult {
  success: number;
  errors: string[];
  totalProcessed: number;
}

interface ImportPreview {
  contracts: Partial<Contract>[];
  isValid: boolean;
  errors: string[];
}

const ImportContracts: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [existingContracts, setExistingContracts] = useState<Contract[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [editingContract, setEditingContract] = useState<Partial<Contract> | null>(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [selectedContractForStaff, setSelectedContractForStaff] = useState<Contract | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Sample CSV template
  const csvTemplate = `contratante,cpf,telefone,email,nomeAniversariante,idadeAniversariante,dataFesta,horarioInicioFesta,enderecoFesta,temaFesta,numeroConvidados,endereco,numero,complemento,bairro,cidade,estado,cep,pacoteAtividades,inicioRecreacao,fimRecreacao,valor,formaPagamento,observacoes
João Silva,123.456.789-00,(11) 99999-9999,joao@email.com,Maria Silva,8,2024-02-15,14:00,Rua das Flores 123,Princesas,25,Rua das Flores,123,Apto 45,Centro,São Paulo,SP,01234-567,Pacote Premium,14:00,18:00,R$ 500,00,PIX,Festa da Maria
Ana Costa,987.654.321-00,(11) 88888-8888,ana@email.com,Pedro Costa,10,2024-02-20,15:00,Av. Paulista 456,Super Heróis,30,Av. Paulista,456,,Bela Vista,São Paulo,SP,01310-000,Pacote VIP,15:00,19:00,R$ 600,00,Cartão,Festa do Pedro`;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      processFile(selectedFile);
    }
  };

  const processFile = async (selectedFile: File) => {
    try {
      const text = await selectedFile.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      
      const contracts: Partial<Contract>[] = [];
      const errors: string[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim());
          const contract: Partial<Contract> = {};
          
          headers.forEach((header, index) => {
            if (values[index]) {
              const value = values[index].trim();
              // Converter tipos específicos
              if (header === 'idadeAniversariante' || header === 'numeroConvidados') {
                (contract as any)[header] = parseInt(value) || 0;
              } else if (header === 'status') {
                // Validar se o status é um dos valores permitidos
                const validStatuses = ['orcamento', 'pendente', 'confirmado', 'concluido', 'cancelado'];
                (contract as any)[header] = validStatuses.includes(value) ? value : 'orcamento';
              } else if (header === 'dataFesta') {
                // Processar data para garantir formato correto
                if (value.includes('/')) {
                  // Se já está em formato DD/MM/YYYY, manter
                  (contract as any)[header] = value;
                } else if (value.includes('-')) {
                  // Se está em formato YYYY-MM-DD, converter para DD/MM/YYYY
                  const parts = value.split('-');
                  if (parts.length === 3) {
                    const [year, month, day] = parts;
                    (contract as any)[header] = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
                  } else {
                    (contract as any)[header] = value;
                  }
                } else {
                  (contract as any)[header] = value;
                }
              } else {
                (contract as any)[header] = value;
              }
            }
          });
          
          // Validate required fields
          if (!contract.contratante || !contract.cpf || !contract.telefone) {
            errors.push(`Linha ${i + 1}: Campos obrigatórios não preenchidos`);
          }
          
          contracts.push(contract);
        }
      }
      
      setPreview({
        contracts,
        isValid: errors.length === 0,
        errors
      });
      
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      alert('Erro ao processar arquivo. Verifique se é um CSV válido.');
    }
  };

  const handleImport = async () => {
    if (!preview || !preview.isValid) return;
    
    setImporting(true);
    try {
      const result = await contractService.importContracts(preview.contracts as Contract[]);
      setImportResult(result);
      
      if (result.success > 0) {
        alert(`${result.success} contratos importados com sucesso!`);
        // Refresh existing contracts
        const contracts = await contractService.getContracts();
        setExistingContracts(contracts);
      }
      
      if (result.errors.length > 0) {
        console.error('Erros na importação:', result.errors);
      }
      
    } catch (error) {
      console.error('Erro na importação:', error);
      alert('Erro durante a importação. Verifique o console para mais detalhes.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_contratos.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditContract = (contract: Partial<Contract>) => {
    setEditingContract(contract);
  };

  const handleSaveEdit = (updatedContract: Partial<Contract>) => {
    if (preview) {
      setPreview({
        ...preview,
        contracts: preview.contracts.map(c => 
          c === editingContract ? updatedContract : c
        )
      });
    }
    setEditingContract(null);
  };

  const removeContract = (index: number) => {
    if (preview) {
      setPreview({
        ...preview,
        contracts: preview.contracts.filter((_, i) => i !== index)
      });
    }
  };

  const openStaffModal = (contract: Contract) => {
    setSelectedContractForStaff(contract);
    setShowStaffModal(true);
  };

  const filteredContracts = existingContracts.filter(contract => {
    if (searchTerm) {
      return contract.contratante?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             contract.nomeAniversariante?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             contract.cpf?.includes(searchTerm);
    }
    if (statusFilter) {
      return contract.status === statusFilter;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="h-8 w-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
                <Upload className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Importar Contratos</h1>
                <p className="text-xs text-slate-500">Importe contratos em lote via CSV</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Upload Section */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-sm mb-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Importar Contratos</h2>
            <p className="text-slate-600 mb-6">
              Faça upload de um arquivo CSV com os dados dos contratos para importação em lote
            </p>
            
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <Download className="w-4 h-4" />
                Baixar Template
              </button>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Upload className="w-4 h-4" />
                Selecionar Arquivo
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {file && (
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <div>
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={clearFile}
                    className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview Section */}
        {preview && (
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Prévia da Importação
              </h3>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  preview.isValid 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-500/20'
                    : 'bg-red-50 text-red-700 border-red-200 ring-red-500/20'
                }`}>
                  {preview.isValid ? (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" />
                      Válido
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-3.5 h-3.5" />
                      Com Erros
                    </>
                  )}
                </span>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-3 py-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {showPreview ? 'Ocultar' : 'Mostrar'} Detalhes
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white/80 rounded-lg p-4 border border-white/20">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{preview.contracts.length}</p>
                  <p className="text-sm text-slate-600">Contratos Encontrados</p>
                </div>
              </div>
              
              <div className="bg-white/80 rounded-lg p-4 border border-white/20">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">
                    {preview.contracts.length - preview.errors.length}
                  </p>
                  <p className="text-sm text-slate-600">Válidos</p>
                </div>
              </div>
              
              <div className="bg-white/80 rounded-lg p-4 border border-white/20">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{preview.errors.length}</p>
                  <p className="text-sm text-slate-600">Com Erros</p>
                </div>
              </div>
            </div>
            
            {preview.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-red-800 mb-2">Erros Encontrados:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {preview.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {showPreview && (
              <div className="bg-white/80 rounded-lg border border-white/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Contratante</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Aniversariante</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200/60">
                      {preview.contracts.map((contract, index) => (
                        <tr key={index} className="hover:bg-slate-50/50">
                          <td className="px-4 py-2 text-sm text-slate-900">
                            {contract.contratante || 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-900">
                            {contract.nomeAniversariante || 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-900">
                            {contract.dataFesta ? formatDate(contract.dataFesta) : 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEditContract(contract)}
                                className="p-1 text-slate-600 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeContract(index)}
                                className="p-1 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remover"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="flex justify-center mt-6">
              <button
                onClick={handleImport}
                disabled={!preview.isValid || importing}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 inline-block mr-2" />
                    Importar Contratos
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Import Results */}
        {importResult && (
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-sm mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Resultado da Importação</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.success}</p>
                  <p className="text-sm text-emerald-700">Importados com Sucesso</p>
                </div>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{importResult.errors.length}</p>
                  <p className="text-sm text-red-700">Erros</p>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResult.totalProcessed}</p>
                  <p className="text-sm text-blue-700">Total Processado</p>
                </div>
              </div>
            </div>
            
            {importResult.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">Erros na Importação:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {importResult.errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Existing Contracts */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200/60">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Contratos Existentes</h3>
              <button
                onClick={() => window.location.reload()}
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="px-6 py-4 bg-slate-50/80 border-b border-slate-200/60">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar contratos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white/80"
                >
                  <option value="">Todos os Status</option>
                  <option value="orcamento">Orçamento</option>
                  <option value="pendente">Pendente</option>
                  <option value="confirmado">Confirmado</option>
                  <option value="concluido">Concluído</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Contracts Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Aniversariante
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Data da Festa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200/60">
                {filteredContracts.slice(0, 10).map((contract) => (
                  <tr key={contract.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center mr-3">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{contract.contratante}</div>
                          <div className="text-sm text-slate-500">{contract.cpf}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{contract.nomeAniversariante}</div>
                      <div className="text-sm text-slate-500">{contract.idadeAniversariante} anos</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {contract.dataFesta ? formatDate(contract.dataFesta) : 'Não definida'}
                      </div>
                      <div className="text-sm text-slate-500">{contract.horarioInicioFesta}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        contract.status === 'confirmado' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        contract.status === 'pendente' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        contract.status === 'orcamento' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-gray-50 text-gray-700 border-gray-200'
                      }`}>
                        {contract.status === 'confirmado' ? 'Confirmado' :
                         contract.status === 'pendente' ? 'Pendente' :
                         contract.status === 'orcamento' ? 'Orçamento' :
                         contract.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                             <div className="flex items-center gap-2">
                         <button className="p-2 text-slate-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors">
                           <Eye className="w-4 h-4" />
                         </button>
                         <button className="p-2 text-slate-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors">
                           <Edit className="w-4 h-4" />
                         </button>
                         <button 
                           onClick={() => openStaffModal(contract)}
                           className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                           title="Escalar Funcionários"
                         >
                           <Users className="w-4 h-4" />
                         </button>
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredContracts.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhum contrato encontrado</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Editar Contrato */}
      {editingContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-900">Editar Contrato</h2>
              <button
                onClick={() => setEditingContract(null)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Dados do Contratante */}
              <div className="border-b border-slate-200 pb-4">
                <h3 className="text-lg font-medium text-slate-900 mb-3">Dados do Contratante</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Contratante *</label>
                    <input
                      type="text"
                      value={editingContract.contratante || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, contratante: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">CPF *</label>
                    <input
                      type="text"
                      value={editingContract.cpf || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, cpf: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Telefone *</label>
                    <input
                      type="tel"
                      value={editingContract.telefone || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, telefone: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={editingContract.email || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, email: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Dados da Festa */}
              <div className="border-b border-slate-200 pb-4">
                <h3 className="text-lg font-medium text-slate-900 mb-3">Dados da Festa</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Nome do Aniversariante *</label>
                    <input
                      type="text"
                      value={editingContract.nomeAniversariante || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, nomeAniversariante: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Idade *</label>
                    <input
                      type="number"
                      value={editingContract.idadeAniversariante || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, idadeAniversariante: parseInt(e.target.value) || 0 } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Data da Festa *</label>
                    <input
                      type="date"
                      value={editingContract.dataFesta ? toISOString(editingContract.dataFesta) : ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, dataFesta: fromISOString(e.target.value) } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Horário de Início *</label>
                    <input
                      type="time"
                      value={editingContract.horarioInicioFesta || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, horarioInicioFesta: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Tema da Festa</label>
                    <input
                      type="text"
                      value={editingContract.temaFesta || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, temaFesta: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Número de Convidados *</label>
                    <input
                      type="number"
                      value={editingContract.numeroConvidados || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, numeroConvidados: parseInt(e.target.value) || 0 } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço da Festa */}
              <div className="border-b border-slate-200 pb-4">
                <h3 className="text-lg font-medium text-slate-900 mb-3">Endereço da Festa</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Endereço da Festa</label>
                    <input
                      type="text"
                      value={editingContract.enderecoFesta || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, enderecoFesta: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Endereço</label>
                    <input
                      type="text"
                      value={editingContract.endereco || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, endereco: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Número</label>
                    <input
                      type="text"
                      value={editingContract.numero || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, numero: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Complemento</label>
                    <input
                      type="text"
                      value={editingContract.complemento || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, complemento: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Bairro</label>
                    <input
                      type="text"
                      value={editingContract.bairro || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, bairro: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Cidade</label>
                    <input
                      type="text"
                      value={editingContract.cidade || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, cidade: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                    <input
                      type="text"
                      value={editingContract.estado || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, estado: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">CEP</label>
                    <input
                      type="text"
                      value={editingContract.cep || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, cep: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Pacote e Horários */}
              <div className="border-b border-slate-200 pb-4">
                <h3 className="text-lg font-medium text-slate-900 mb-3">Pacote e Horários</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Pacote de Atividades *</label>
                    <input
                      type="text"
                      value={editingContract.pacoteAtividades || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, pacoteAtividades: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Início da Recreação *</label>
                    <input
                      type="time"
                      value={editingContract.inicioRecreacao || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, inicioRecreacao: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Fim da Recreação *</label>
                    <input
                      type="time"
                      value={editingContract.fimRecreacao || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, fimRecreacao: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Restrição Musical</label>
                    <input
                      type="text"
                      value={editingContract.restricaoMusical || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, restricaoMusical: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Valores e Status */}
              <div className="border-b border-slate-200 pb-4">
                <h3 className="text-lg font-medium text-slate-900 mb-3">Valores e Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Valor</label>
                    <input
                      type="text"
                      value={editingContract.valor || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, valor: e.target.value } : null)}
                      placeholder="R$ 0,00"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Forma de Pagamento</label>
                    <input
                      type="text"
                      value={editingContract.formaPagamento || ''}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, formaPagamento: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                    <select
                      value={editingContract.status || 'orcamento'}
                      onChange={(e) => setEditingContract(prev => prev ? { ...prev, status: e.target.value as Contract['status'] } : null)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="orcamento">Orçamento</option>
                      <option value="pendente">Pendente</option>
                      <option value="confirmado">Confirmado</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Observações</label>
                <textarea
                  value={editingContract.observacoes || ''}
                  onChange={(e) => setEditingContract(prev => prev ? { ...prev, observacoes: e.target.value } : null)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Adicione observações sobre o contrato..."
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
              <button
                onClick={() => setEditingContract(null)}
                className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => editingContract && handleSaveEdit(editingContract)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
                 </div>
       )}

       {/* Modal Escalar Funcionários */}
       {showStaffModal && selectedContractForStaff && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
             <div className="flex items-center justify-between mb-6">
               <h2 className="text-xl font-semibold text-slate-900">Escalar Funcionários</h2>
               <button
                 onClick={() => setShowStaffModal(false)}
                 className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>
             
             <div className="mb-6">
               <h3 className="text-lg font-medium text-slate-900 mb-3">Contrato: {selectedContractForStaff.contratante}</h3>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
                 <div>
                   <span className="font-medium">Aniversariante:</span> {selectedContractForStaff.nomeAniversariante}
                 </div>
                 <div>
                   <span className="font-medium">Data:</span> {formatDate(selectedContractForStaff.dataFesta)}
                 </div>
                 <div>
                   <span className="font-medium">Horário:</span> {selectedContractForStaff.horarioInicioFesta}
                 </div>
               </div>
             </div>

             <div className="space-y-6">
               {/* Funcionários Disponíveis */}
               <div>
                 <h4 className="text-md font-medium text-slate-900 mb-3">Funcionários Disponíveis</h4>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                   {[
                     { id: '1', nome: 'Ana Silva', cargo: 'Animadora', especialidades: ['Pintura Facial', 'Oficina de Artes'] },
                     { id: '2', nome: 'Carlos Santos', cargo: 'Animador', especialidades: ['Show de Mágica', 'Personagens'] },
                     { id: '3', nome: 'Mariana Costa', cargo: 'Coordenadora', especialidades: ['Coordenação', 'Logística'] },
                     { id: '4', nome: 'Roberto Lima', cargo: 'Animador', especialidades: ['Gincanas', 'Brincadeiras'] },
                     { id: '5', nome: 'Juliana Alves', cargo: 'Animadora', especialidades: ['Contação de Histórias', 'Música'] },
                     { id: '6', nome: 'Pedro Oliveira', cargo: 'Animador', especialidades: ['Esportes', 'Dança'] }
                   ].map(employee => (
                     <div key={employee.id} className="border border-slate-200 rounded-lg p-4 hover:border-primary/50 transition-colors">
                       <div className="flex items-start justify-between mb-3">
                         <div>
                           <h5 className="font-medium text-slate-900">{employee.nome}</h5>
                           <p className="text-sm text-slate-600">{employee.cargo}</p>
                         </div>
                         <button className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm hover:bg-emerald-200 transition-colors">
                           Escalar
                         </button>
                       </div>
                       <div className="space-y-1">
                         {employee.especialidades.map((especialidade, index) => (
                           <span key={index} className="inline-block px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs mr-2 mb-1">
                             {especialidade}
                           </span>
                         ))}
                       </div>
                     </div>
                   ))}
                 </div>
               </div>

               {/* Funcionários Escalados */}
               <div>
                 <h4 className="text-md font-medium text-slate-900 mb-3">Funcionários Escalados</h4>
                 <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                   <p className="text-slate-500 text-center">Nenhum funcionário escalado ainda</p>
                 </div>
               </div>
             </div>

             <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
               <button
                 onClick={() => setShowStaffModal(false)}
                 className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
               >
                 Fechar
               </button>
               <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                 Confirmar Escalação
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default ImportContracts;



